<script setup>
import { computed, inject, nextTick, onBeforeUnmount, onMounted, ref } from "vue";
import ArgonSnackbar from "@/components/ArgonSnackbar.vue";
import { routeStore } from "@/_store/routeStore";
import { decaStore } from "@/_store/decaStore";
import { errorUtils, fileUtils } from "@/utils";
import KanbanBoardHeader from "./KanbanBoardHeader.vue";
import StopCard from "./StopCard.vue";
import StopDetailModal from "./stop-detail/StopDetailModal.vue";
import RouteDetailModal from "@/views/routes/components/RouteDetailModal.vue";
import MoveRouteStopModal from "./MoveRouteStopModal.vue";
import RouteStopInstructionsModal from "./RouteStopInstructionsModal.vue";
import DecaReviewModal from "./DecaReviewModal.vue";
import CreateUnloadStopModal from "./CreateUnloadStopModal.vue";
import CreateLoadStopModal from "./CreateLoadStopModal.vue";
import { usePlannerKanban } from "../composables/usePlannerKanban";
import { vSortable } from "../directives/vSortable";
import { getAvailableRouteVehicleOptions } from "../utils/routeVehicleOptions";
import {
  canDeletePlanRoutes,
  isPlanEditable,
  isPlanFinalized,
  isPlanInProgress,
  normalizePlanStatus,
} from "../utils/planStatus";
import { formatTimeWindow, normalizeAlerts } from "../../../utils/formatters";

const props = defineProps({
  planId: {
    type: String,
    default: "",
  },
  planInfo: {
    type: Object,
    default() {
      return null;
    },
  },
  dragDisabled: {
    type: Boolean,
    default: false,
  },
  sortableOptions: {
    type: Object,
    default() {
      return {};
    },
  },
});

const {
  assignedRouteBoards,
  vehicleOptions,
  getRouteDragGroup,
  getRouteAccentColor,
  getRouteVehicleContext,
  handleDragStart,
  applyLocalDragUpdate,
} = usePlannerKanban();
const emit = defineEmits(["kanban-action"]);
const routesStore = routeStore();
const decaStores = decaStore();

const sweetAlert = inject("$swal", null);
const UNASSIGNED_BOARD_ID = "_unassigned";
const selectedStop = ref(null);
const selectedRouteId = ref("");
const showStopDetailModal = ref(false);
const showRouteDetailModal = ref(false);
const showMoveRouteStopModal = ref(false);
const selectedMoveRouteStopId = ref("");
const selectedMoveSourceRouteId = ref("");
const showRouteStopInstructionsModal = ref(false);
const showDecaReviewModal = ref(false);
const decaReviewStopId = ref("");
const selectedInstructionsRouteStopId = ref("");
const selectedInstructionsRouteId = ref("");
const selectedInstructionsText = ref("");
const showCreateUnloadStopModal = ref(false);
const selectedUnloadStopRouteId = ref("");
const selectedUnloadStopNewOrder = ref(1);
const selectedUnloadStopId = ref("");
const showCreateLoadStopModal = ref(false);
const selectedLoadStopRouteId = ref("");
const selectedLoadStopNewOrder = ref(1);
const selectedLoadStopId = ref("");
const snackbar = ref(null);
const hasBoardScroll = ref(false);
let boardScrollContainer = null;

function updateBoardScrollState() {
  hasBoardScroll.value = (boardScrollContainer?.scrollTop || 0) > 0;
}

function bindBoardScrollListener() {
  if (typeof document === "undefined") return;
  boardScrollContainer = document.querySelector(".kanban-dp-shell");
  if (!boardScrollContainer) return;
  updateBoardScrollState();
  boardScrollContainer.addEventListener("scroll", updateBoardScrollState, {
    passive: true,
  });
}

onMounted(() => {
  nextTick(() => {
    bindBoardScrollListener();
  });
});

onBeforeUnmount(() => {
  if (!boardScrollContainer) return;
  boardScrollContainer.removeEventListener("scroll", updateBoardScrollState);
  boardScrollContainer = null;
});

function setDragNoSelect(isActive) {
  if (typeof document === "undefined") return;
  if (isActive) {
    document.body?.classList?.add("kanban-drag-no-select");
    return;
  }
  document.body?.classList?.remove("kanban-drag-no-select");
}

function onDragStart(event) {
  setDragNoSelect(true);
  handleDragStart(event);
}

function isLoadUnloadManualType(manualType) {
  return manualType === "CARGA" || manualType === "DESCARGA";
}

function getBoardSortableOptions(routeBoard) {
  return {
    ...props.sortableOptions,
    group: getRouteDragGroup(routeBoard),
    animation: 150,
    draggable: ".kanban-dp-item",
    ghostClass: "drag-ghost",
    chosenClass: "drag-chosen",
    dragClass: "drag-active",
    fallbackOnBody: true,
    swapThreshold: 0.65,
    disabled: props.dragDisabled || isFinalizedPlan.value,
    multiDrag: true,
    multiDragKey: "ctrl",
    selectedClass: "sortable-selected",
    filter: ".stop-route-actions-button, .actions-dropdown-menu",
    preventOnFilter: false,
    onStart: onDragStart,
    onEnd: onDragEnd,
  };
}

function toSafeArray(value) {
  return Array.isArray(value) ? value : [];
}

function resolveVehicleId(routeData) {
  return routeData?.vehicle_id || "";
}

function resolveRouteId(routeData, boardId) {
  return routeData?._id || routeData?.id || boardId || "";
}

function isUnassignedBoard(board) {
  return board?.id === UNASSIGNED_BOARD_ID;
}

const availableRouteVehicleOptions = computed(() =>
  getAvailableRouteVehicleOptions(
    assignedRouteBoards.value,
    vehicleOptions.value
  )
);

const visibleRouteBoards = computed(() =>
  assignedRouteBoards.value.filter(
    (routeBoard) =>
      !routeBoard?.isPlaceholder ||
      (isPlanEditable(planStatus.value) &&
        availableRouteVehicleOptions.value.length > 0)
  )
);

const planStatus = computed(() =>
  normalizePlanStatus(props.planInfo?.status)
);
const isFinalizedPlan = computed(() => isPlanFinalized(planStatus.value));
const canDeleteRoutes = computed(() => canDeletePlanRoutes(planStatus.value));

const routeBoardActions = computed(() => {
  return [
    [
      {
        label: "Detalle (doble clic)",
        event: "detailRoute",
      }
    ],   
    [
      {
        label: "Parada de carga",
        event: "createLoadStop",
        disabled: !isPlanEditable(planStatus.value),
      },
      {
        label: "Parada de descarga",
        event: "createUnloadStop",
        disabled: !isPlanEditable(planStatus.value),
      }
      
    ],
    [
      {
        label: "App Móvil",
        event: "mobileFlow",
        disabled: !isPlanInProgress(planStatus.value),
      }
    ],
    [
      {
        label: "Eliminar",
        event: "deleteRoute",
        type: "danger",
        disabled: !canDeleteRoutes.value,
      },
    ],
  ];
});

function isStopPending(stopItem) {
  return String(stopItem?.status || "").toUpperCase() === "PENDIENTE";
}

function getRouteStopActions(stopItem) {
  const isLoadUnload = isLoadUnloadManualType(stopItem?.manual_type);
  const isEntrega = String(stopItem?.type || "").toUpperCase() === "ENTREGA";
  const actions = [
    [
      {
        label: "Detalle (doble clic)",
        event: "detail",
      },
    ],
    [
      ...(isLoadUnload
        ? []
        : [
            {
              label: "Cambiar de ruta",
              event: "moveToVehicle",
              disabled: isFinalizedPlan.value,
            },
          ]),
      {
        label: "Instrucciones",
        event: "editInstructions",
        disabled: isFinalizedPlan.value || !isStopPending(stopItem),
      },
    ],
  ];

  if (isLoadUnload) {
    actions[1].push({
      label: "Modificar",
      event: "modifyLoadUnload",
      disabled: isFinalizedPlan.value,
    });
  }

  if (isEntrega) {
    // No sabemos aqui si el stop ya tiene un DeCA generado (has_deca no
    // viaja en la data del tablero) — el propio DecaReviewModal detecta y
    // permite modificarlo igual al abrirlo, solo el texto del item se queda
    // siempre en "Generar DeCA" en vez de "Modificar DeCA" si ya existe.
    const status = String(stopItem?.status || "").toUpperCase();
    const isStopClosed = ["FINALIZADO", "FINALIZADO_PARCIAL", "INCIDENCIA"].includes(status);
    actions.push([
      {
        label: "Generar DeCA",
        event: "generateDeca",
        disabled: !isPlanEditable(planStatus.value) || isStopClosed,
        tooltip: isStopClosed
          ? "La parada ya está finalizada, no se puede generar/modificar su DeCA."
          : "",
      },
      {
        label: "Imprimir DeCA",
        event: "printDeca",
      },
    ]);
  }

  actions.push([
    {
      label: "Desasignar",
      event: "unassign",
      disabled: isFinalizedPlan.value,
    },
  ]);

  return actions;
}

function getRouteData(routeBoard) {
  return routeBoard?.apiRoute || {};
}

function getRouteMetrics(board) {
  if (isUnassignedBoard(board) || board?.isPlaceholder) return [];

  const routeData = board?.apiRoute || {};
  const routeDistance = routeData?.km ?? routeData?.distance ?? "";
  const routeTimeWindow = formatTimeWindow(routeData?.time_window);
  const routeTotalTime = routeData?.total_time ?? 0;
  const stopCount =
    board?.ui?.stops_counter ?? toSafeArray(board?.items).length;

  const metrics = [
    {
      icon: "fas fa-map-marker-alt",
      value: String(stopCount),
    },
    {
      icon: "fas fa-route",
      value: String(routeDistance ?? 0),
    },
    {
      icon: "fas fa-clock",
      value: routeTimeWindow || "",
    },
    {
      icon: "fas fa-stopwatch",
      value: routeTotalTime,
    },
  ];

  const warnings = normalizeAlerts(routeData?.alerts);
  if (warnings.length > 0) {
    metrics.push({
      icon: "fas fa-exclamation-circle",
      value: warnings,
    });
  }

  return metrics;
}

function collectSelectedRouteStopIds(routeId, primaryRouteStopId) {
  const board = assignedRouteBoards.value.find((b) => b.id === routeId);
  if (!board) return [primaryRouteStopId];

  const primaryCard = board.items.find((c) => c.apiStop?._id === primaryRouteStopId);
  if (!primaryCard) return [primaryRouteStopId];

  const boardEl = document.querySelector(`[data-board-id="${routeId}"]`);
  const primaryCardEl = boardEl?.querySelector(`[data-task-id="${primaryCard.id}"]`);

  if (!primaryCardEl?.classList.contains("sortable-selected")) {
    return [primaryRouteStopId];
  }

  const ids = Array.from(boardEl.querySelectorAll(".kanban-dp-item.sortable-selected"))
    .map((el) => {
      const card = board.items.find((c) => String(c.id) === el.dataset.taskId);
      return card?.apiStop?._id || "";
    })
    .filter(Boolean);

  return ids.length > 0 ? ids : [primaryRouteStopId];
}

function getBoardName(routeBoard) {
  if (routeBoard?.isPlaceholder) return "Nueva ruta";
  const routeData = getRouteData(routeBoard);
  return routeData.name || "Sin vehiculo";
}

function getBoardVehicleId(routeBoard) {
  const routeData = getRouteData(routeBoard);
  return routeData.vehicle_id || "";
}

function getBoardProgress(routeBoard) {
  const value = Number(routeBoard?.ui?.progress);
  return Number.isFinite(value) ? value : 0;
}
function closeSnackbar() {
  snackbar.value = null;
}

function closeStopDetailModal() {
  showStopDetailModal.value = false;
  selectedStop.value = null;
}

function closeRouteDetailModal() {
  showRouteDetailModal.value = false;
  selectedRouteId.value = "";
}

function closeMoveRouteStopModal() {
  showMoveRouteStopModal.value = false;
  selectedMoveRouteStopId.value = "";
  selectedMoveSourceRouteId.value = "";
}

function openRouteStopInstructionsModal({ routeStopId, routeId, instructions }) {
  selectedInstructionsRouteStopId.value = routeStopId;
  selectedInstructionsRouteId.value = routeId;
  selectedInstructionsText.value = instructions || "";
  showRouteStopInstructionsModal.value = true;
}

function closeRouteStopInstructionsModal() {
  showRouteStopInstructionsModal.value = false;
  selectedInstructionsRouteStopId.value = "";
  selectedInstructionsRouteId.value = "";
  selectedInstructionsText.value = "";
}

function openDecaReviewForStop(stopItem) {
  const stopId = stopItem?.stop_id || "";
  if (!stopId) {
    snackbar.value = {
      title: "Error",
      message: "No se encontro stop_id para generar el DeCA.",
      type: "danger",
      color: "white",
    };
    return;
  }
  decaReviewStopId.value = stopId;
  showDecaReviewModal.value = true;
}

function closeDecaReviewModal() {
  showDecaReviewModal.value = false;
  decaReviewStopId.value = "";
}

function onDecaReviewConfirm() {
  closeDecaReviewModal();
  snackbar.value = {
    title: "OK",
    message: "DeCA guardado correctamente",
    type: "info",
    color: "white",
  };
}

function printDecaForStop(stopItem) {
  const stopId = stopItem?.stop_id || "";
  if (!stopId) {
    snackbar.value = {
      title: "Error",
      message: "No se encontro stop_id para imprimir el DeCA.",
      type: "danger",
      color: "white",
    };
    return;
  }

  decaStores
    .printDeca([stopId])
    .then((bytes) => {
      fileUtils.openBytesFile(bytes, "application/pdf");
    })
    .catch((error) => {
      snackbar.value = {
        title: "Error",
        message: errorUtils.getErrorMessageHtml(
          error,
          "No existe un DeCA generado para esta parada."
        ),
        type: "danger",
        color: "white",
      };
    });
}

function onRouteStopInstructionsConfirm(payload) {
  const routeStopId = selectedInstructionsRouteStopId.value;
  const routeId = selectedInstructionsRouteId.value;
  if (!routeStopId || !routeId) {
    snackbar.value = {
      title: "Error",
      message: "No se pudo resolver la información para guardar las instrucciones.",
      type: "danger",
      color: "white",
    };
    return;
  }

  emit("kanban-action", {
    type: "update-route-stop-instructions",
    payload: {
      routeId,
      routeStopId,
      instructions: payload?.instructions || "",
    },
  });

  closeRouteStopInstructionsModal();
}

function openCreateUnloadStopModal(routeBoard) {
  const routeId = resolveRouteId(routeBoard?.apiRoute, routeBoard?.id);
  if (!routeId) {
    snackbar.value = {
      title: "Error",
      message: "No se encontro route_id para crear la parada de descarga.",
      type: "danger",
      color: "white",
    };
    return;
  }

  snackbar.value = null;
  selectedUnloadStopRouteId.value = routeId;
  selectedUnloadStopNewOrder.value =
    (getRouteData(routeBoard)?.routes?.length || 0) + 1;
  showCreateUnloadStopModal.value = true;
}

function closeCreateUnloadStopModal() {
  showCreateUnloadStopModal.value = false;
  selectedUnloadStopRouteId.value = "";
  selectedUnloadStopNewOrder.value = 1;
  selectedUnloadStopId.value = "";
}

function openModifyUnloadStopModal(routeId, stopItem) {
  const stopId = stopItem?.stop_id || "";
  if (!routeId || !stopId) {
    snackbar.value = {
      title: "Error",
      message: "No se encontro la parada de descarga a modificar.",
      type: "danger",
      color: "white",
    };
    return;
  }

  snackbar.value = null;
  selectedUnloadStopRouteId.value = routeId;
  selectedUnloadStopNewOrder.value = 1;
  selectedUnloadStopId.value = stopId;
  showCreateUnloadStopModal.value = true;
}

function onCreateUnloadStopConfirm(payload) {
  const routeId = selectedUnloadStopRouteId.value;
  if (!routeId) {
    closeCreateUnloadStopModal();
    return;
  }

  emit("kanban-action", {
    type: "create-unload-stop",
    payload: {
      routeId,
      warehouseId: payload?.warehouseId || "",
      taskIds: payload?.taskIds || [],
      newOrder: selectedUnloadStopNewOrder.value,
      stopId: payload?.stopId || null,
    },
  });

  closeCreateUnloadStopModal();
}

function openCreateLoadStopModal(routeBoard) {
  const routeId = resolveRouteId(routeBoard?.apiRoute, routeBoard?.id);
  if (!routeId) {
    snackbar.value = {
      title: "Error",
      message: "No se encontro route_id para crear la parada de carga.",
      type: "danger",
      color: "white",
    };
    return;
  }

  snackbar.value = null;
  selectedLoadStopRouteId.value = routeId;
  // A diferencia de descarga (que se añade al final), la carga se inserta
  // al inicio de la ruta: conceptualmente ocurre antes de las entregas. El
  // jefe de trafico reordena la tarjeta despues donde corresponda.
  selectedLoadStopNewOrder.value = 1;
  showCreateLoadStopModal.value = true;
}

function closeCreateLoadStopModal() {
  showCreateLoadStopModal.value = false;
  selectedLoadStopRouteId.value = "";
  selectedLoadStopNewOrder.value = 1;
  selectedLoadStopId.value = "";
}

function openModifyLoadStopModal(routeId, stopItem) {
  const stopId = stopItem?.stop_id || "";
  if (!routeId || !stopId) {
    snackbar.value = {
      title: "Error",
      message: "No se encontro la parada de carga a modificar.",
      type: "danger",
      color: "white",
    };
    return;
  }

  snackbar.value = null;
  selectedLoadStopRouteId.value = routeId;
  selectedLoadStopNewOrder.value = 1;
  selectedLoadStopId.value = stopId;
  showCreateLoadStopModal.value = true;
}

function onCreateLoadStopConfirm(payload) {
  const routeId = selectedLoadStopRouteId.value;
  if (!routeId) {
    closeCreateLoadStopModal();
    return;
  }

  emit("kanban-action", {
    type: "create-load-stop",
    payload: {
      routeId,
      warehouseId: payload?.warehouseId || "",
      taskIds: payload?.taskIds || [],
      newOrder: selectedLoadStopNewOrder.value,
      stopId: payload?.stopId || null,
    },
  });

  closeCreateLoadStopModal();
}

function openRouteStopDetail(stopItem) {
  const stopId = stopItem?.stop_id || "";
  if (!stopId) {
    snackbar.value = {
      title: "Error",
      message: "No se encontro stop_id para consultar el detalle.",
      type: "danger",
      color: "white",
    };
    return;
  }

  selectedStop.value = stopItem;
  showStopDetailModal.value = true;
}

function openRouteBoardDetail(routeBoard) {
  const routeId = resolveRouteId(routeBoard?.apiRoute, routeBoard?.id);
  if (!routeId) {
    snackbar.value = {
      title: "Error",
      message: "No se encontro route_id para consultar el detalle.",
      type: "danger",
      color: "white",
    };
    return;
  }

  snackbar.value = null;
  selectedRouteId.value = routeId;
  showRouteDetailModal.value = true;
  routesStore.getRouteById(routeId).catch(() => {});
}

function openMoveRouteStopModal({ routeStopId, sourceRouteId }) {
  snackbar.value = null;
  selectedMoveRouteStopId.value = routeStopId || "";
  selectedMoveSourceRouteId.value = sourceRouteId || "";
  showMoveRouteStopModal.value = true;
}

function onMoveRouteStopConfirm(payload) {
  const targetRouteId = payload?.targetRouteId || "";
  const routeStopId = selectedMoveRouteStopId.value;
  const sourceRouteId = selectedMoveSourceRouteId.value;

  if (!targetRouteId || !routeStopId || !sourceRouteId) {
    snackbar.value = {
      title: "Error",
      message: "No se pudo resolver la información para mover la parada.",
      type: "danger",
      color: "white",
    };
    return;
  }

  const targetRouteBoard = assignedRouteBoards.value.find(
    (board) =>
      board?.id === targetRouteId &&
      !board?.isPlaceholder &&
      !isUnassignedBoard(board)
  );

  if (!targetRouteBoard) {
    snackbar.value = {
      title: "Error",
      message: "No se encontro la ruta destino seleccionada.",
      type: "danger",
      color: "white",
    };
    return;
  }

  const destinationStopsCount = toSafeArray(targetRouteBoard?.items).length;
  emit("kanban-action", {
    type: "move-route-stop",
    payload: {
      sourceRouteId,
      routeStopIds: [routeStopId],
      targetRouteId,
      newOrder: destinationStopsCount + 1,
    },
  });

  closeMoveRouteStopModal();
}

async function updateRouteBoardVehicle(boardId, vehicleId) {
  const board = assignedRouteBoards.value.find((item) => item?.id === boardId);
  const normalizedVehicleId = vehicleId;
  if (!board) {
    throw new Error("No se encontro el tablero para asignar el vehículo.");
  }
  if (!normalizedVehicleId) {
    throw new Error("Debe seleccionar un vehículo válido.");
  }
  if (!board.isPlaceholder) return;

  const alreadyUsed = assignedRouteBoards.value.some(
    (routeBoard) =>
      routeBoard?.id !== boardId &&
      !routeBoard?.isPlaceholder &&
      !isUnassignedBoard(routeBoard) &&
      resolveVehicleId(routeBoard?.apiRoute) === normalizedVehicleId
  );
  if (alreadyUsed) {
    throw new Error("El vehículo seleccionado ya está asignado a otra ruta.");
  }

  const selectedVehicle = vehicleOptions.value.find(
    (item) => item?.value === normalizedVehicleId
  );
  if (!selectedVehicle) {
    throw new Error("No se encontro el vehículo seleccionado.");
  }

  emit("kanban-action", {
    type: "assign-vehicle",
    payload: {
      boardId,
      vehicleId: normalizedVehicleId,
    },
  });
}

async function onRouteStopAction(payload) {
  const actionEvent = payload?.action?.event;
  const stopItem = payload?.stopItem;
  if (!stopItem || !actionEvent) return;

  if (actionEvent === "detail") {
    openRouteStopDetail(stopItem);
    return;
  }

  if (actionEvent === "moveToVehicle") {
    const routeStopId = stopItem?._id || "";
    const sourceRouteId = payload?.vehicle?.id || "";
    openMoveRouteStopModal({ routeStopId, sourceRouteId });
    return;
  }

  if (actionEvent === "editInstructions") {
    const routeStopId = stopItem?._id || "";
    const routeId = payload?.vehicle?.id || "";
    openRouteStopInstructionsModal({
      routeStopId,
      routeId,
      instructions: stopItem?.instructions || "",
    });
    return;
  }

  if (actionEvent === "generateDeca") {
    openDecaReviewForStop(stopItem);
    return;
  }

  if (actionEvent === "printDeca") {
    printDecaForStop(stopItem);
    return;
  }

  if (actionEvent === "modifyLoadUnload") {
    const routeId = payload?.vehicle?.id || "";
    if (stopItem?.manual_type === "CARGA") {
      openModifyLoadStopModal(routeId, stopItem);
    } else {
      openModifyUnloadStopModal(routeId, stopItem);
    }
    return;
  }

  if (actionEvent !== "unassign") return;

  const routeId = payload?.vehicle?.id || "";
  if (!routeId) {
    snackbar.value = {
      title: "Error",
      message: "No se encontro route_id para desasignar la parada.",
      type: "danger",
      color: "white",
    };
    return;
  }

  const primaryRouteStopId = stopItem?._id || "";
  if (!primaryRouteStopId) {
    snackbar.value = {
      title: "Error",
      message: "No se encontro _id de route_stop para desasignar la parada.",
      type: "danger",
      color: "white",
    };
    return;
  }

  const routeStopIds = collectSelectedRouteStopIds(routeId, primaryRouteStopId);
  emit("kanban-action", {
    type: "unassign-route-stop",
    payload: {
      routeId,
      routeStopIds,
    },
  });
}

async function onRouteBoardVehicleChange(routeBoard, vehicleId) {
  try {
    await updateRouteBoardVehicle(routeBoard?.id, vehicleId);
    snackbar.value = null;
  } catch (error) {
    snackbar.value = {
      title: "Error",
      message: error?.message || "No se pudo asignar el vehículo a la ruta.",
      type: "danger",
      color: "white",
    };
  }
}

async function onRouteBoardAction(routeBoard, action) {
  if (action?.event === "detailRoute") {
    openRouteBoardDetail(routeBoard);
    return;
  }

  if (action?.event === "mobileFlow") {
    emit("kanban-action", {
      type: "mobile-flow-open",
      payload: {
        planId: props.planId || "",
        vehicleId: getBoardVehicleId(routeBoard) || "",
      },
    });
    return;
  }

  if (action?.event === "createUnloadStop") {
    openCreateUnloadStopModal(routeBoard);
    return;
  }

  if (action?.event === "createLoadStop") {
    openCreateLoadStopModal(routeBoard);
    return;
  }

  if (action?.event !== "deleteRoute") return;

  const confirmOptions = {
    title: "Estás seguro de querer eliminar esta ruta?",
    text: "¡No podrá¡ revertir esta acción!",
    showCancelButton: true,
    confirmButtonText: "Eliminar",
    cancelButtonText: "Cancelar",
    reverseButtons: true,
    customClass: {
      confirmButton: "btn btn-xs ms-1 bg-gradient-danger",
      cancelButton: "btn btn-xs btn-outline-primary me-1",
    },
    buttonsStyling: false,
  };

  const result =
    typeof sweetAlert === "function"
      ? await sweetAlert(confirmOptions)
      : {
          isConfirmed: window.confirm(
            "¿Está¡ seguro de querer eliminar esta ruta?"
          ),
        };
  if (!result?.isConfirmed) return;

  const routeId = resolveRouteId(routeBoard?.apiRoute, routeBoard?.id);
  if (!routeId) {
    snackbar.value = {
      title: "Error",
      message: "No se encontro route_id para eliminar la ruta.",
      type: "danger",
      color: "white",
    };
    return;
  }

  emit("kanban-action", {
    type: "delete-route",
    payload: {
      routeId,
    },
  });
}

function onDragEnd(event) {
  setDragNoSelect(false);
  applyLocalDragUpdate(event);
  emit("kanban-action", {
    type: "drag-drop",
    payload: {
      event,
    },
  });
}
</script>

<template>
  <section
    v-for="routeBoard in visibleRouteBoards"
    :key="routeBoard.id"
    :class="[
      routeBoard.isPlaceholder ? 'kanban-add-board' : 'kanban-dp-board',
    ]"
    :style="{
      '--route-board-accent': routeBoard.isPlaceholder
        ? 'transparent'
        : getRouteAccentColor(routeBoard),
    }"
  >
    <header
      class="kanban-dp-header"
      :class="{ 'is-scroll-accent': hasBoardScroll }"
    >
      <kanban-board-header
        :key="routeBoard.id"
        :board-id="routeBoard.id"
        :title="getBoardName(routeBoard)"
        :accent-color="getRouteAccentColor(routeBoard)"
        :vehicle-id="getBoardVehicleId(routeBoard)"
        :vehicle-options="vehicleOptions"

        :plan-status="planStatus"

        :metrics="getRouteMetrics(routeBoard)"
        :progress-value="getBoardProgress(routeBoard)"
        :is-selectable="Boolean(routeBoard.isPlaceholder)"
        :show-metrics="
          !routeBoard.isPlaceholder && !isUnassignedBoard(routeBoard)
        "
        :show-progress="
          !routeBoard.isPlaceholder && !isUnassignedBoard(routeBoard)
        "
        :show-action="!routeBoard.isPlaceholder && !isFinalizedPlan"
        :actions="routeBoardActions"
        :detail-on-dblclick="!routeBoard.isPlaceholder"
        :selectable-vehicles="availableRouteVehicleOptions"
        @update:vehicle-id="
          (value) => onRouteBoardVehicleChange(routeBoard, value)
        "
        @action="(action) => onRouteBoardAction(routeBoard, action)"
        @detail="openRouteBoardDetail(routeBoard)"
      />
    </header>

    <div
      v-if="!routeBoard.isPlaceholder"
      v-sortable="getBoardSortableOptions(routeBoard)"
      class="kanban-dp-drag"
      :data-board-id="routeBoard.id"
    >
      <div
        v-if="!routeBoard.isPlaceholder && !routeBoard.items?.length"
        class="planner-drop-hint"
        aria-hidden="true"
      >
        <span class="planner-drop-hint-label">Arrastra quí las paradas</span>
      </div>
      <article
        v-for="routeStopCard in routeBoard.items"
        :key="routeStopCard.id"
        class="kanban-dp-item border-radius-md"
        :data-task-id="routeStopCard.id"
        :data-manual="routeStopCard.apiStop?.manual_type || ''"
      >
        <stop-card
          :stop-item="routeStopCard.apiStop"
          :vehicle="getRouteVehicleContext(routeBoard)"
          :actions="getRouteStopActions(routeStopCard.apiStop)"
          :show-menu="!isFinalizedPlan"
          :menu-disabled="dragDisabled"
          @open="openRouteStopDetail"
          @action="onRouteStopAction"
        />
      </article>
    </div>
  </section>

  <stop-detail-modal
    v-if="showStopDetailModal && selectedStop"
    modal-id="route-stop-detail-modal"
    :stop-id="selectedStop.stop_id"
    @close="closeStopDetailModal"
  />

  <route-detail-modal
    v-if="showRouteDetailModal && selectedRouteId"
    modal-id="route-board-detail-modal"
    :route-id="selectedRouteId"
    @close="closeRouteDetailModal"
  />

  <deca-review-modal
    v-if="showDecaReviewModal && decaReviewStopId"
    modal-id="route-board-deca-review-modal"
    :stop-id="decaReviewStopId"
    @close="closeDecaReviewModal"
    @confirm="onDecaReviewConfirm"
  />

  <move-route-stop-modal
    v-if="
      showMoveRouteStopModal &&
      selectedMoveRouteStopId &&
      selectedMoveSourceRouteId
    "
    modal-id="move-route-stop-modal"
    :plan-id="planId"
    :route-stop-id="selectedMoveRouteStopId"
    :source-route-id="selectedMoveSourceRouteId"
    @confirm="onMoveRouteStopConfirm"
    @close="closeMoveRouteStopModal"
  />

  <route-stop-instructions-modal
    v-if="showRouteStopInstructionsModal && selectedInstructionsRouteStopId"
    modal-id="route-stop-instructions-modal"
    :instructions="selectedInstructionsText"
    @confirm="onRouteStopInstructionsConfirm"
    @close="closeRouteStopInstructionsModal"
  />

  <create-unload-stop-modal
    v-if="showCreateUnloadStopModal && selectedUnloadStopRouteId"
    modal-id="create-unload-stop-modal"
    :route-id="selectedUnloadStopRouteId"
    :stop-id="selectedUnloadStopId"
    @confirm="onCreateUnloadStopConfirm"
    @close="closeCreateUnloadStopModal"
  />

  <create-load-stop-modal
    v-if="showCreateLoadStopModal && selectedLoadStopRouteId"
    modal-id="create-load-stop-modal"
    :route-id="selectedLoadStopRouteId"
    :stop-id="selectedLoadStopId"
    @confirm="onCreateLoadStopConfirm"
    @close="closeCreateLoadStopModal"
  />

  <div class="position-fixed top-0 end-0 z-index-2 p-3">
    <argon-snackbar
      v-if="snackbar"
      :title="snackbar.title"
      :description="snackbar.message"
      :icon="{ component: 'ni ni-notification-70', color: snackbar.type }"
      :color="snackbar.color"
      :close-handler="closeSnackbar"
    />
  </div>
</template>

<style scoped lang="scss">
@use "./planner-surface-shared";

.kanban-dp-board {
  @extend .planner-board-shell;
  box-shadow: inset 0 2px 0 0 var(--route-board-accent, transparent),
    inset 0 0 0 1px var(--planner-border, #dbe2ee),
    var(--planner-shadow-sm, 0 1px 2px rgba(16, 24, 40, 0.04));
  margin-right: 6px;
  position: relative;
}

.kanban-add-board {
  background: #ffffff;
  border: 1px solid transparent;
  border-radius: var(--planner-board-shell-radius, 18px);
  box-shadow: inset 0 2px 0 0 var(--route-board-accent, transparent),
    inset 0 0 0 1px var(--planner-border, #dbe2ee),
    var(--planner-shadow-sm, 0 1px 2px rgba(16, 24, 40, 0.04));
  display: flex;
  flex: 0 0 var(--planner-board-width, 220px);
  flex-direction: column;
  margin-right: 6px;
  min-height: 0;
  padding: var(--planner-board-shell-padding, 8px);
  align-self: flex-start;
  width: var(--planner-board-width, 220px);
}

.kanban-dp-board:last-child {
  margin-right: 0;
}

.kanban-add-board:last-child {
  margin-right: 0;
}

.kanban-dp-board.kanban-dropdown-open {
  z-index: 230;
}

.kanban-add-board.kanban-dropdown-open {
  z-index: 230;
}

.kanban-dp-header {
  @extend .planner-board-header-row;
}

.kanban-dp-header.is-scroll-accent :deep(.kanban-board-header) {
  box-shadow: inset 0 2px 0 0 var(--route-board-accent, transparent);
}

.kanban-add-board .kanban-dp-header {
  display: block;
  flex: 0 0 auto;
  margin-bottom: 0;
  min-height: 0;
}

.kanban-dp-drag {
  @extend .planner-drop-surface;
  overflow: visible;
}

.kanban-dp-item {
  @extend .planner-kanban-item;
}

.kanban-dp-item:hover {
  z-index: 90;
}

.kanban-dp-item.kanban-dropdown-open {
  z-index: 220;
}

.kanban-dp-item:focus-within {
  z-index: 170;
}
</style>





